import React, { useEffect, useState } from "react";
import styled, { keyframes } from "styled-components";
import { useSelector } from "react-redux";
import CircularProgress from "@mui/material/CircularProgress";
import { auth, db, functions } from "../authentication/firebase";
import { doc, getDoc } from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { useNavigate } from "react-router";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";

function SubscriptionDetail({ priceInfo }) {
  const subscription = useSelector((state) => state.user.subscription);
  const [loading, setLoading] = useState(false);
  const [productData, setProductData] = useState(null);
  const [priceData, setPriceData] = useState(null);
  const [fetchingProduct, setFetchingProduct] = useState(true);
  const user = auth.currentUser;
  const navigate = useNavigate();

  // Determine subscription status
  const isSubscribed = subscription?.status === "active";
  const isCanceled = subscription?.cancel_at_period_end === true;
  
  // Format dates for display
  const subscriptionStartDate = subscription?.current_period_start
    ? new Date(
        subscription.current_period_start.seconds * 1000
      ).toLocaleDateString()
    : null;
  
  const subscriptionEndDate = subscription?.current_period_end
    ? new Date(
        subscription.current_period_end.seconds * 1000
      ).toLocaleDateString()
    : null;
  
  // Get cancellation date if exists
  const cancellationDate = subscription?.canceled_at
    ? new Date(subscription.canceled_at.seconds * 1000).toLocaleDateString()
    : null;
  
  const planType = subscription?.items?.some(
    (item) => item.plan.interval === "year"
  )
    ? "Yearly"
    : "Monthly";

  // Fetch product data from Firebase
  useEffect(() => {
    const fetchProductData = async () => {
      if (!subscription?.items?.[0]?.price?.product?.id) {
        setFetchingProduct(false);
        return;
      }

      try {
        const productId = subscription.items[0].price.product.id;
        const priceId = subscription.items[0].price.id;
        
        // Fetch product data
        const productDoc = await getDoc(doc(db, "products", productId));
        if (productDoc.exists()) {
          setProductData(productDoc.data());
        }

        // Fetch price data
        const priceDoc = await getDoc(
          doc(db, "products", productId, "prices", priceId)
        );
        if (priceDoc.exists()) {
          setPriceData({ id: priceDoc.id, ...priceDoc.data() });
        }
      } catch (error) {
        console.error("Error fetching product data:", error);
      } finally {
        setFetchingProduct(false);
      }
    };

    if (isSubscribed) {
      fetchProductData();
    } else {
      setFetchingProduct(false);
    }
  }, [subscription, isSubscribed]);

  // Create customer portal session for managing subscription
  const createPortalSession = async () => {
    setLoading(true);
    try {
      // This uses the extension's createPortalLink function
      const createPortalLink = httpsCallable(
        functions,
        "ext-firestore-stripe-payments-createPortalLink"
      );
      const { data } = await createPortalLink({
        returnUrl: window.location.origin + window.location.pathname,
      });
      
      // Redirect to the portal
      if (data.url) {
        window.location.assign(data.url);
      }
    } catch (error) {
      console.error("Error creating portal session:", error);
      setLoading(false);
    }
  };

  const LoadingSkeleton = () => (
    <PricingCardContainer>
      <SkeletonCard>
        <SkeletonBadge />
        <SkeletonTitle />
        <SkeletonText />
        
        <SkeletonBenefits>
          {[...Array(4)].map((_, idx) => (
            <SkeletonBenefit key={idx}>
              <SkeletonIcon />
              <SkeletonBenefitText />
            </SkeletonBenefit>
          ))}
        </SkeletonBenefits>
        
        <SkeletonDetails>
          <SkeletonDetailItem />
          <SkeletonDetailItem />
        </SkeletonDetails>
        
        <SkeletonButton />
      </SkeletonCard>
    </PricingCardContainer>
  );

  if (fetchingProduct) {
    return (
      <StyledDiv>
        <LoadingSkeleton />
      </StyledDiv>
    );
  }

  return (
    <>
      <StyledDiv>
        {isSubscribed && productData && priceData ? (
          <PricingCardContainer>
            <StyledPricingCard isCurrentPlan={true}>
              <div>
                <CurrentPlanBadge>Current Plan</CurrentPlanBadge>
                <h3>{productData.name}</h3>
                {/* <div className="price">
                  <span className="amount">${priceData.unit_amount / 100}</span>
                  <span className="period">/{priceData.interval}</span>
                </div> */}
                <p className="plan-for">{productData.for}</p>
              </div>

              <ul className="benefits">
                {productData.benefits?.map((benefit, idx) => (
                  <li key={idx}>
                    <CheckCircleOutlineIcon className="check-icon" />
                    {benefit}
                  </li>
                ))}
              </ul>

              <CurrentPlanSection>
                <SubscriptionDetails>
                  <DetailItem>
                    <span className="label">Started:</span>
                    <span className="value">{subscriptionStartDate}</span>
                  </DetailItem>
                  <DetailItem>
                    <span className="label">Renews:</span>
                    <span className="value">{subscriptionEndDate}</span>
                  </DetailItem>
                  {isCanceled && (
                    <CancelledNotice>
                      Subscription cancelled - active until{" "}
                      {subscriptionEndDate}
                    </CancelledNotice>
                  )}
                </SubscriptionDetails>
                
                <ButtonContainer>
                  {isCanceled && (
                    <ResumeButton onClick={createPortalSession}>
                      {loading ? (
                        <CircularProgress size={20} color="inherit" />
                      ) : (
                        "Resume Subscription"
                      )}
                    </ResumeButton>
                  )}
                  <button className="primary-btn" onClick={createPortalSession}>
                    {loading ? (
                      <CircularProgress size={20} color="inherit" />
                    ) : (
                      "Manage Billing"
                    )}
                  </button>
                </ButtonContainer>
              </CurrentPlanSection>
            </StyledPricingCard>
          </PricingCardContainer>
        ) : (
          <NoSubscriptionContainer>
            <NoSubscriptionMessage>
              No active subscription found.
            </NoSubscriptionMessage>
            <button className="primary-btn" onClick={() => navigate('/subscribe')}>
              Adopt a Tree
            </button>
          </NoSubscriptionContainer>
        )}
      </StyledDiv>
    </>
  );
}

// Skeleton Animation
const shimmer = keyframes`
  0% {
    background-position: -468px 0;
  }
  100% {
    background-position: 468px 0;
  }
`;

const SkeletonCard = styled.div`
  overflow: hidden;
  width: 100%;
  max-width: 400px;
  min-width: 320px;
  border-radius: 24px;
  padding: 32px;
  background: white;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
  display: flex;
  flex-direction: column;
  gap: 32px;
`;

const SkeletonElement = styled.div`
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 400% 100%;
  animation: ${shimmer} 4.2s ease-in-out infinite;
  border-radius: 4px;
`;

const SkeletonBadge = styled(SkeletonElement)`
  height: 32px;
  width: 120px;
  border-radius: 20px;
  margin-bottom: 16px;
`;

const SkeletonTitle = styled(SkeletonElement)`
  height: 32px;
  width: 200px;
  margin-bottom: 8px;
`;

const SkeletonText = styled(SkeletonElement)`
  height: 18px;
  width: 150px;
`;

const SkeletonBenefits = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin: 24px 0;
`;

const SkeletonBenefit = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const SkeletonIcon = styled(SkeletonElement)`
  width: 20px;
  height: 20px;
  border-radius: 50%;
  flex-shrink: 0;
`;

const SkeletonBenefitText = styled(SkeletonElement)`
  height: 16px;
  flex: 1;
`;

const SkeletonDetails = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const SkeletonDetailItem = styled(SkeletonElement)`
  height: 14px;
  width: 100%;
`;

const SkeletonButton = styled(SkeletonElement)`
  height: 44px;
  width: 100%;
  border-radius: 8px;
  margin-top: 20px;
`;

const PricingCardContainer = styled.div`
  display: flex;
  justify-content: center;
  width: 100%;
`;

const StyledPricingCard = styled.div`
  overflow: hidden;
  width: 100%;
  max-width: 400px;
  min-width: 320px;
  border-radius: 24px;
  padding: 32px;
  border: 2px solid transparent;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
  display: flex;
  flex-direction: column;
  gap: 32px;
  transition: all 0.3s ease;
  position: relative;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);

  h3 {
    font-size: 28px;
    color: #333;
    margin: 0;
    font-weight: 600;
  }

  .price {
    display: flex;
    align-items: baseline;
    gap: 4px;
    margin-bottom: 8px;

    .amount {
      font-size: 42px;
      font-weight: bold;
      color: #333;
    }

    .period {
      font-size: 18px;
      color: #666;
    }
  }

  .plan-for {
    color: #666;
    font-size: 18px;
    margin: 0;
  }

  .benefits {
    list-style-type: none;
    padding: 0;
    margin: 24px 0;
    display: flex;
    flex-direction: column;
    gap: 16px;
    min-height: 200px;

    li {
      display: flex;
      align-items: center;
      color: #444;
      font-size: 16px;
      line-height: 1.5;

      .check-icon {
        color: var(--primary-dark-color);
        margin-right: 12px;
        font-size: 20px;
      }
    }
  }
`;

const CurrentPlanSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const CurrentPlanBadge = styled.div`
  background: linear-gradient(135deg, #97aafd 0%, #e971ff 100%);
  color: white;
  padding: 8px 16px;
  border-radius: 20px;
  text-align: center;
  font-weight: 600;
  font-size: 14px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 16px;
`;

const SubscriptionDetails = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const DetailItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 14px;

  .label {
    color: #666;
    font-weight: 500;
  }

  .value {
    color: #333;
    font-weight: 600;
  }
`;

const CancelledNotice = styled.div`
  background-color: #fff3cd;
  color: #856404;
  padding: 8px 12px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 500;
  text-align: center;
  border-left: 3px solid #ffeeba;
`;

const ButtonContainer = styled.div`
  display: flex;
  gap: 1rem;
  margin-top: 20px;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
  button{
    padding: 8px 24px;
  }
`;

const StyledDiv = styled.section`
  min-height: 50vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 40px;
  background-color: #f9fafb;
`;

const NoSubscriptionContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 1.5rem;
  min-height: 300px;
`;

const NoSubscriptionMessage = styled.p`
  font-size: 1.3rem;
  color: var(--text-light);
  margin: 0;
`;

const AdoptTreeButton = styled.button`
  background-color: var(--primary-color);
  color: white;
  border: none;
  padding: 12px 24px;
  font-size: 1.1rem;
  border-radius: 8px;
  cursor: pointer;
  font-weight: 600;
  transition: all 0.3s ease;

  &:hover {
    background-color: var(--primary-dark-color);
    transform: translateY(-2px);
  }
`;

const ResumeButton = styled.button`
  background-color: #27ae60;
  color: white;
  border: none;
  padding: 12px 25px;
  font-size: 1rem;
  border-radius: 8px;
  cursor: pointer;
  transition: background-color 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 140px;
  flex: 1;

  &:hover {
    background-color: #2ecc71;
  }
`;

export default SubscriptionDetail;
